import { mediaUrl } from '../lib/api';
import type { PostMediaItem, PostShape } from '../types';
import { Spinner } from './Ui';

type Props = {
  /** Точный текст сообщения с сервера. */
  messageHtml: string | null;
  media: PostMediaItem[];
  buttonText: string | null;
  /** Передан — кнопка в превью становится полем ввода. */
  onButtonTextChange?: (value: string) => void;
  buttonPlaceholder?: string;
  channelTitle?: string | null;
  loading?: boolean;
  /** Чем именно уйдёт пост — от этого зависит вид кнопки. */
  shape?: PostShape | null;
};

/**
 * Показывает пост так, как он придёт в канал.
 *
 * Форма зависит от содержимого, и автору важно знать заранее: у альбома
 * Telegram не принимает inline-кнопку, поэтому ссылка на подборку уходит
 * строкой в подписи, а слишком длинный текст — вторым сообщением.
 */
const ПОЯСНЕНИЕ: Partial<Record<PostShape, string>> = {
  album:
    'Файлов несколько — Telegram не разрешает прикрепить к альбому кнопку. Ссылка на подборку уйдёт строкой в конце текста, нажимается так же.',
  'album+text':
    'Текст не помещается в подпись к фото, поэтому уйдёт вторым сообщением — кнопка будет при нём.',
};
export function PostPreview({
  messageHtml,
  media,
  buttonText,
  onButtonTextChange,
  buttonPlaceholder,
  channelTitle,
  shape,
  loading,
}: Props) {
  // Файлы, поставленные внутрь текста, показываем на их местах, а в шапке
  // остаются только те, что автор туда не двигал.
  const inlineIds = new Set(
    [...(messageHtml ?? '').matchAll(/<tg-media id="([a-z0-9]+)"><\/tg-media>/g)].map((m) => m[1]!),
  );
  const headerMedia = media.filter((item) => !inlineIds.has(item.id));
  const bodyHtml = (messageHtml ?? '').replace(
    /<tg-media id="([a-z0-9]+)"><\/tg-media>/g,
    (_match, id: string) => {
      const item = media.find((m) => m.id === id);
      if (!item) return '';
      return `<span class="preview__inline"><img src="${mediaUrl(item.url)}" alt=""/></span>`;
    },
  );

  return (
    <div className="preview">
      <div className="preview__label">
        Как это придёт в канал{channelTitle ? ` «${channelTitle}»` : ''}
      </div>

      <div className={`preview__bubble ${headerMedia.length > 0 ? 'preview__bubble--with-album' : ''}`}>
        {headerMedia.length > 0 && (
          <div className="preview__media-slot">
            <div className={`preview__album preview__album--n${Math.min(headerMedia.length, 4)}`}>
              {headerMedia.map((item) =>
                item.kind === 'video' ? (
                  <div className="preview__cell" key={item.id}>
                    <video src={mediaUrl(item.url)} muted playsInline preload="metadata" />
                    <span className="preview__play">▶</span>
                  </div>
                ) : (
                  <div className="preview__cell" key={item.id}>
                    <img src={mediaUrl(item.url)} alt={item.originalName} />
                  </div>
                ),
              )}
            </div>
          </div>
        )}

        {/* Спиннер только пока текста нет вообще. Подменять им уже показанный
            текст нельзя: блок менял высоту на каждое нажатие клавиши, и вся
            страница дёргалась. */}
        {loading && messageHtml === null ? (
          <Spinner />
        ) : bodyHtml ? (
          // HTML пришёл с сервера уже очищенным до тегов, которые понимает
          // Telegram: b/i/u/s/a/code/pre/blockquote/tg-spoiler.
          <div className="preview__text" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        ) : (
          <div className="preview__text muted">Текста пока нет</div>
        )}

        {/* Кнопка редактируется прямо здесь — так видно, как она будет
            выглядеть в посте, ещё до публикации. */}
        {onButtonTextChange ? (
          <input
            className="preview__button preview__button--editable"
            value={buttonText ?? ''}
            onChange={(e) => onButtonTextChange(e.target.value)}
            placeholder={buttonPlaceholder ?? 'Текст кнопки'}
            maxLength={64}
            aria-label="Текст кнопки под постом"
          />
        ) : (
          buttonText && <div className="preview__button">{buttonText}</div>
        )}
      </div>

      {/* Форма поста меняется от содержимого, и узнать об этом после
          публикации — плохо. Поясняем заранее и только когда есть что
          пояснять. */}
      {shape && ПОЯСНЕНИЕ[shape] && <p className="preview__note">{ПОЯСНЕНИЕ[shape]}</p>}
    </div>
  );
}
